import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { trackSignUp } from '../lib/analytics'
import { APP_NAME } from '../lib/constants'

export function SignUp() {
  const navigate = useNavigate()
  const { signUp } = useAuth()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== confirmPassword) {
      setError('كلمتا المرور غير متطابقتين')
      return
    }

    if (password.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل')
      return
    }

    if (name.trim().length < 2) {
      setError('الاسم يجب أن يكون حرفين على الأقل')
      return
    }

    setLoading(true)

    try {
      await signUp(email, password, name.trim())
      
      // Track successful sign up
      trackSignUp('email')
      
      // After successful signup, navigate to home
      navigate('/')
    } catch (err) {
      console.error('Sign up error:', err)
      const errorMessage = err instanceof Error ? err.message : String(err)
      if (errorMessage.includes('already registered')) {
        setError('هذا البريد الإلكتروني مسجل بالفعل')
      } else if (errorMessage.includes('Password')) {
        setError('كلمة المرور ضعيفة جدًا')
      } else {
        setError('حدث خطأ أثناء إنشاء الحساب. يرجى المحاولة مرة أخرى.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen px-4 py-12 bg-slate-900">
      <div className="w-full max-w-md space-y-8">
        <PageHeader />
        <SignUpFormCard
          name={name}
          email={email}
          password={password}
          confirmPassword={confirmPassword}
          loading={loading}
          error={error}
          onNameChange={setName}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onConfirmPasswordChange={setConfirmPassword}
          onSubmit={handleSubmit}
        />
        <BackToHomeLink />
      </div>
    </div>
  )
}

function PageHeader() {
  return (
    <div className="text-center">
      <h1 className="mb-2 text-4xl font-bold text-amber-500">{APP_NAME.ar}</h1>
      <h2 className="mb-2 text-2xl font-semibold text-slate-100">إنشاء حساب جديد</h2>
      <p className="text-slate-400">انضم إلينا في رحلة تعريب اللغة المصرية</p>
    </div>
  )
}

interface SignUpFormCardProps {
  name: string
  email: string
  password: string
  confirmPassword: string
  loading: boolean
  error: string | null
  onNameChange: (name: string) => void
  onEmailChange: (email: string) => void
  onPasswordChange: (password: string) => void
  onConfirmPasswordChange: (confirmPassword: string) => void
  onSubmit: (e: React.FormEvent) => void
}

function SignUpFormCard({
  name,
  email,
  password,
  confirmPassword,
  loading,
  error,
  onNameChange,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
}: SignUpFormCardProps) {
  return (
    <div className="p-8 border rounded-lg bg-slate-800 border-slate-700">
      <form onSubmit={onSubmit} className="space-y-6">
        {error && <ErrorMessage message={error} />}
        <NameField value={name} onChange={onNameChange} disabled={loading} />
        <EmailField value={email} onChange={onEmailChange} disabled={loading} />
        <PasswordField value={password} onChange={onPasswordChange} disabled={loading} />
        <ConfirmPasswordField value={confirmPassword} onChange={onConfirmPasswordChange} disabled={loading} />
        <SubmitButton loading={loading} />
      </form>
      <SignInPrompt />
    </div>
  )
}

interface ErrorMessageProps {
  message: string
}

function ErrorMessage({ message }: ErrorMessageProps) {
  return (
    <div className="p-4 border rounded-lg bg-rose-500/10 border-rose-500/50">
      <p className="text-sm text-rose-400">{message}</p>
    </div>
  )
}

interface NameFieldProps {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}

function NameField({ value, onChange, disabled }: NameFieldProps) {
  return (
    <div>
      <label htmlFor="name" className="block mb-2 text-sm font-medium text-slate-300">
        الاسم
      </label>
      <input
        id="name"
        type="text"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 border rounded-lg bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        placeholder="أحمد حسن"
        disabled={disabled}
      />
    </div>
  )
}

interface EmailFieldProps {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}

function EmailField({ value, onChange, disabled }: EmailFieldProps) {
  return (
    <div>
      <label htmlFor="email" className="block mb-2 text-sm font-medium text-slate-300">
        البريد الإلكتروني
      </label>
      <input
        id="email"
        type="email"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 border rounded-lg bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-left"
        placeholder="your@email.com"
        disabled={disabled}
      />
    </div>
  )
}

interface PasswordFieldProps {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}

function PasswordField({ value, onChange, disabled }: PasswordFieldProps) {
  return (
    <div>
      <label htmlFor="password" className="block mb-2 text-sm font-medium text-slate-300">
        كلمة المرور
      </label>
      <input
        id="password"
        type="password"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 border rounded-lg bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-left"
        placeholder="••••••••"
        disabled={disabled}
      />
      <p className="mt-1 text-xs text-slate-500">6 أحرف على الأقل</p>
    </div>
  )
}

interface ConfirmPasswordFieldProps {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}

function ConfirmPasswordField({ value, onChange, disabled }: ConfirmPasswordFieldProps) {
  return (
    <div>
      <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium text-slate-300">
        تأكيد كلمة المرور
      </label>
      <input
        id="confirmPassword"
        type="password"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 border rounded-lg bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-left"
        placeholder="••••••••"
        disabled={disabled}
      />
    </div>
  )
}

interface SubmitButtonProps {
  loading: boolean
}

function SubmitButton({ loading }: SubmitButtonProps) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full px-4 py-3 font-semibold transition-colors duration-200 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {loading ? 'جاري إنشاء الحساب...' : 'إنشاء حساب'}
    </button>
  )
}

function SignInPrompt() {
  return (
    <div className="mt-6 text-center">
      <p className="text-slate-400">
        لديك حساب بالفعل؟{' '}
        <Link
          to="/signin"
          className="font-semibold transition-colors text-amber-500 hover:text-amber-400"
        >
          تسجيل الدخول
        </Link>
      </p>
    </div>
  )
}

function BackToHomeLink() {
  return (
    <div className="text-center">
      <Link to="/" className="text-sm transition-colors text-slate-400 hover:text-slate-300">
        ← العودة للصفحة الرئيسية
      </Link>
    </div>
  )
}
